import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Field,
  Input,
  PageHeader,
  Select,
  Toolbar,
} from '@biovet/ui';
import { auth } from '../lib/actor.js';
import { MasterTable, Messages, useAction } from './document.js';
import { apiSend, useResource, type AdminWriteResult } from '../lib/api.js';

/**
 * Product categories — the catalogue's two levels.
 *
 * A group and the categories in it, and products hang on the second level. The
 * depth limit is the API's (`masters.ts`), not the schema's: `product_categories`
 * would carry four levels and the tree machinery would maintain them, but a
 * catalogue that can nest indefinitely is one every report has to flatten and
 * nobody can navigate.
 *
 * **The four account columns are what make this more than a filing system.** A
 * category names the inventory, sales, COGS and purchase accounts that every
 * product under it inherits when the product leaves its own blank — which is how
 * a four-hundred-SKU catalogue avoids four hundred account mappings. Only a leaf
 * account may be named: a group head takes no posting, so a category pointing at
 * one would give its products an account the ledger refuses.
 *
 * **Neither the code nor those four accounts are asked for on a create**, and
 * the server refuses both in the body rather than ignoring them. The code is
 * derived from the name because it is the SKU prefix every product in the
 * category will carry, and the accounts are opened by `ensure_category_sub_ledger`
 * when the row lands. All five remain editable on the category that exists,
 * where an accountant re-pointing one can see what it points at now.
 */

interface CategoryRow {
  id: string;
  code: string;
  name: string;
  depth: number;
  isActive: boolean;
  parentCode: string | null;
  parentName: string | null;
  inventoryAccount: string | null;
  salesAccount: string | null;
  cogsAccount: string | null;
  purchaseAccount: string | null;
  childCount: number;
  productCount: number;
}

interface CategoryRefs {
  categories: CategoryRow[];
  parents: Array<{ code: string; name: string }>;
  accounts: Array<{ code: string; name: string; type: string }>;
  maxDepth: number;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

type Draft = {
  mode: 'create' | 'edit';
  code: string;
  name: string;
  parentCode: string;
  inventoryAccount: string;
  salesAccount: string;
  cogsAccount: string;
  purchaseAccount: string;
  isActive: boolean;
};

const BLANK: Draft = {
  mode: 'create',
  code: '',
  name: '',
  parentCode: '',
  inventoryAccount: '',
  salesAccount: '',
  cogsAccount: '',
  purchaseAccount: '',
  isActive: true,
};

/**
 * The code a name will be issued, as far as the browser can know it.
 *
 * A deliberate second copy of `nextCategoryCode` in `apps/api/src/masters.ts`,
 * kept for the same reason `voucher-model.ts` restates `0015_invariants.sql`:
 * the server is still the authority, and this exists so somebody naming a
 * category sees the SKU prefix they are choosing before they save rather than
 * in the confirmation afterwards. The one thing it cannot reproduce is the `-2`
 * the server appends when the stem is already taken — that needs the register.
 * **If you change the derivation there, change it here.**
 */
function codeStem(name: string): string {
  return (
    name
      .normalize('NFKD')
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 34)
      .replace(/-+$/, '') || (name.trim() ? 'CAT' : '')
  );
}

/** The four defaults, so the form and the payload cannot disagree about them. */
const ACCOUNT_FIELDS = [
  ['inventoryAccount', 'Inventory account', 'Where stock of these products sits — 1151, 1152'],
  ['salesAccount', 'Sales account', 'Revenue when they are sold — 4100'],
  ['cogsAccount', 'COGS account', 'Cost when they leave the warehouse — 5300'],
  ['purchaseAccount', 'Purchase account', 'Cost when they are bought — 5110, 5120'],
] as const;

export function CategoriesScreen() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const res = useResource<CategoryRefs>('/masters/categories', {
    ...auth(),
    include: includeInactive ? 'inactive' : undefined,
  });
  const categories = useMemo(() => res.data?.categories ?? [], [res.data]);
  const { busy, notice, problem, run } = useAction(res.reload);
  const [draft, setDraft] = useState<Draft | null>(null);

  const accountOptions = res.data?.accounts ?? [];
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  const save = () =>
    void run(async () => {
      if (!draft) throw new Error('No draft');
      // A create sends the name and where it goes, and nothing else. The code is
      // derived from the name and the four accounts are opened by the chart —
      // the server refuses either of them in the body rather than ignoring it,
      // so sending `null` would be a 422 rather than a blank.
      const result =
        draft.mode === 'create'
          ? await apiSend<AdminWriteResult>(
              'POST',
              '/masters/categories',
              { name: draft.name, parentCode: draft.parentCode || null },
              auth(),
            )
          : await apiSend<AdminWriteResult>(
              'PATCH',
              `/masters/categories/${encodeURIComponent(draft.code)}`,
              {
                name: draft.name,
                inventoryAccount: draft.inventoryAccount || null,
                salesAccount: draft.salesAccount || null,
                cogsAccount: draft.cogsAccount || null,
                purchaseAccount: draft.purchaseAccount || null,
                isActive: draft.isActive,
              },
              auth(),
            );
      setDraft(null);
      return result;
    });

  return (
    <>
      <PageHeader
        breadcrumb="Inventory / Categories"
        title="Product categories"
        description="Group your products into categories so they inherit the right accounts."
      />

      <Messages notice={notice} problem={problem} />

      {draft && (
        <Card flush className="mb-3.5" data-noprint="1">
          <CardHeader
            title={draft.mode === 'create' ? 'New category' : `Edit ${draft.code}`}
            subtitle={
              draft.mode === 'edit'
                ? 'The code and the parent are fixed. Re-parenting would change which accounts every product under it inherits, without saying so — move the products instead.'
                : 'Leave the group blank to create a top-level group; name one to create a category inside it. The code and the four accounts are issued.'
            }
            actions={
              <Toolbar>
                <Button size="sm" onClick={() => setDraft(null)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  loading={busy}
                  disabled={!draft.name}
                  onClick={save}
                >
                  Save
                </Button>
              </Toolbar>
            }
          />
          <CardBody className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Field label="Name" required>
              <Input value={draft.name} onChange={(e) => set('name', e.target.value)} />
            </Field>
            {draft.mode === 'edit' ? (
              <Field label="Code" hint="Issued from the name; it is the SKU prefix">
                <Input value={draft.code} disabled />
              </Field>
            ) : (
              /*
               * The issued code, shown before it is issued rather than after.
               * It is the SKU prefix every product in this category will carry,
               * so somebody naming a category is choosing that prefix whether
               * or not the screen says so. The server derives the same stem and
               * appends `-2` where it is taken, which is the one part the
               * browser cannot know.
               */
              <Field label="Code" hint="Issued from the name; it is the SKU prefix">
                <Input value={codeStem(draft.name)} disabled placeholder="—" />
              </Field>
            )}
            <Field
              label="Inside group"
              hint={
                draft.mode === 'edit' ? 'Fixed once created' : 'Blank makes this a group itself'
              }
            >
              <Select
                value={draft.parentCode}
                disabled={draft.mode === 'edit'}
                onChange={(e) => set('parentCode', e.target.value)}
              >
                <option value="">— top level —</option>
                {(res.data?.parents ?? []).map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.code} · {p.name}
                  </option>
                ))}
              </Select>
            </Field>
            {draft.mode === 'edit' && (
              <Field label="Active">
                <Checkbox
                  checked={draft.isActive}
                  onChange={(e) => set('isActive', e.target.checked)}
                  label="Offered when a product is filed"
                />
              </Field>
            )}
            {/*
              The four accounts are an edit, never a create. Inventory is the
              warehouse's account on every movement and nothing resolves a
              category's purchase account at all, so two of the four decided
              nothing; the other two are opened by the chart when the category
              lands. Asking for all four of a person filing a category was four
              questions with no answer they could give better than the chart.
            */}
            {draft.mode === 'edit' &&
              ACCOUNT_FIELDS.map(([key, label, hint]) => (
                <Field key={key} label={label} hint={hint}>
                  <Select value={draft[key]} onChange={(e) => set(key, e.target.value)}>
                    <option value="">— inherit —</option>
                    {accountOptions.map((a) => (
                      <option key={a.code} value={a.code}>
                        {a.code} · {a.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              ))}
          </CardBody>
        </Card>
      )}

      <MasterTable
        title="Categories"
        subtitle="A category holding products cannot be deleted — those products inherit their accounts from it"
        loading={res.loading}
        error={res.error}
        empty="No categories"
        onNew={res.data?.canCreate ? () => setDraft({ ...BLANK }) : undefined}
        newLabel="New category"
        toggle={{
          on: includeInactive,
          onToggle: () => setIncludeInactive((v) => !v),
          label: 'Show inactive',
        }}
        columns={['Code', 'Name', 'Accounts', 'Sub-categories', 'Products', '']}
      >
        {categories.map((c) => (
          <tr key={c.id} className="bb-row-h border-b border-[var(--line-2)]">
            <td className="bb-mono px-[var(--pad)] text-[12.5px] font-medium">
              {/*
                The indent alone is the hierarchy — the rule `UsersZones.tsx`
                already records. A `└` draws the last branch of a tree, so a
                column of them under one group claims every child is the last
                one; the four sub-categories of Feed Raw Material are siblings,
                not four terminal branches. The list is ordered by parent, so
                the children are contiguous under theirs and the indent is the
                whole picture. Both columns indent, or the name column reads as
                a flat list beside a hierarchical code.
              */}
              <span style={{ paddingLeft: `${c.depth * 16}px` }}>{c.code}</span>
            </td>
            <td className="px-2.5 text-[13px]">
              <span style={{ paddingLeft: `${c.depth * 16}px` }}>{c.name}</span>
              {c.depth === 0 && (
                <Badge tone="info" className="ml-2">
                  Group
                </Badge>
              )}
              {!c.isActive && (
                <Badge tone="mute" className="ml-2">
                  Inactive
                </Badge>
              )}
            </td>
            <td className="bb-mono px-2.5 text-[11.5px] text-[var(--ink-2)]">
              {[c.inventoryAccount, c.salesAccount, c.cogsAccount, c.purchaseAccount]
                .filter(Boolean)
                .join(' · ') || '— inherited —'}
            </td>
            <td className="bb-num px-2.5 text-right text-[12.5px]">{c.childCount || '—'}</td>
            <td className="bb-num px-2.5 text-right text-[12.5px]">{c.productCount || '—'}</td>
            <td className="px-[var(--pad)] text-right" data-noprint="1">
              <Toolbar>
                <Button
                  size="sm"
                  disabled={!res.data?.canEdit}
                  onClick={() =>
                    setDraft({
                      mode: 'edit',
                      code: c.code,
                      name: c.name,
                      parentCode: c.parentCode ?? '',
                      inventoryAccount: c.inventoryAccount ?? '',
                      salesAccount: c.salesAccount ?? '',
                      cogsAccount: c.cogsAccount ?? '',
                      purchaseAccount: c.purchaseAccount ?? '',
                      isActive: c.isActive,
                    })
                  }
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  loading={busy}
                  disabled={!res.data?.canDelete || c.childCount > 0 || c.productCount > 0}
                  onClick={() =>
                    void run(() =>
                      apiSend<AdminWriteResult>(
                        'DELETE',
                        `/masters/categories/${encodeURIComponent(c.code)}`,
                        undefined,
                        auth(),
                      ),
                    )
                  }
                >
                  Delete
                </Button>
              </Toolbar>
            </td>
          </tr>
        ))}
      </MasterTable>
    </>
  );
}
